import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type {
  JourneyId,
  LearnerProjectionV1,
  LearningEventType,
  LearningEventV1,
  LocalIdentity,
  ResumeCheckpoint,
} from "@seeing-maths/contracts";
import { createLearnerProjection } from "@seeing-maths/learning-core";
import { IndexedDbPersistence } from "@seeing-maths/persistence";

interface EventDraft {
  type: LearningEventType;
  payload: Record<string, unknown>;
  questionTemplateId?: string | null;
  questionSeed?: string | null;
  widgetId?: string | null;
}

interface LearningContextValue {
  ready: boolean;
  identity: LocalIdentity | null;
  projection: LearnerProjectionV1 | null;
  recentEvents: LearningEventV1[];
  emit: (conceptId: JourneyId, draft: EventDraft) => Promise<LearnerProjectionV1>;
  emitMany: (
    conceptId: JourneyId,
    drafts: EventDraft[],
  ) => Promise<LearnerProjectionV1>;
  saveCheckpoint: (
    conceptId: JourneyId,
    checkpoint: ResumeCheckpoint,
  ) => Promise<LearnerProjectionV1>;
  resetLocalLearner: () => Promise<void>;
}

const LearningContext = createContext<LearningContextValue | null>(null);
const persistence = new IndexedDbPersistence();

export function LearningProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [identity, setIdentity] = useState<LocalIdentity | null>(null);
  const [projection, setProjection] = useState<LearnerProjectionV1 | null>(null);
  const [recentEvents, setRecentEvents] = useState<LearningEventV1[]>([]);
  const initialized = useRef(false);
  const sessionId = useRef(`session-${crypto.randomUUID()}`);
  const clientSequence = useRef(0);
  const queue = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    void (async () => {
      const localIdentity = await persistence.getOrCreateIdentity();
      const [loadedProjection, events] = await Promise.all([
        persistence.loadProjection(localIdentity.learnerId),
        persistence.listEvents(localIdentity.learnerId, 50),
      ]);
      clientSequence.current = events.at(-1)?.clientSequence ?? 0;
      setIdentity(localIdentity);
      setProjection(loadedProjection);
      setRecentEvents(events);
      setReady(true);
    })();
  }, []);

  const emitMany = useCallback(
    async (conceptId: JourneyId, drafts: EventDraft[]) => {
      if (!identity) throw new Error("Local learner identity is not ready.");
      const events = drafts.map((draft) => {
        const eventId = `event-${crypto.randomUUID()}`;
        return {
          eventId,
          schemaVersion: "1.0.0" as const,
          occurredAt: new Date().toISOString(),
          sessionId: sessionId.current,
          learnerId: identity.learnerId,
          conceptId,
          conceptVersion: "1.0.0",
          questionTemplateId: draft.questionTemplateId ?? null,
          questionTemplateVersion: draft.questionTemplateId ? "1.0.0" : null,
          questionSeed: draft.questionSeed ?? null,
          widgetId: draft.widgetId ?? null,
          type: draft.type,
          payload: draft.payload,
          clientSequence: ++clientSequence.current,
          idempotencyKey: eventId,
          deviceId: identity.deviceId,
        } satisfies LearningEventV1;
      });

      let result = projection ?? createLearnerProjection(identity.learnerId);
      const task = queue.current.then(async () => {
        const appended = await persistence.appendEvents(events);
        result = appended.projection;
        const latest = await persistence.listEvents(identity.learnerId, 50);
        setProjection(appended.projection);
        setRecentEvents(latest);
      });
      queue.current = task.catch(() => undefined);
      await task;
      return result;
    },
    [identity, projection],
  );

  const emit = useCallback(
    (conceptId: JourneyId, draft: EventDraft) => emitMany(conceptId, [draft]),
    [emitMany],
  );

  const saveCheckpoint = useCallback(
    async (conceptId: JourneyId, checkpoint: ResumeCheckpoint) => {
      if (!identity) throw new Error("Local learner identity is not ready.");
      await queue.current;
      const saved = await persistence.saveCheckpoint(
        identity.learnerId,
        conceptId,
        checkpoint,
      );
      setProjection(saved);
      return saved;
    },
    [identity],
  );

  const resetLocalLearner = useCallback(async () => {
    if (!identity) return;
    await queue.current;
    await persistence.resetLearner(identity.learnerId);
    setProjection(createLearnerProjection(identity.learnerId));
    setRecentEvents([]);
    clientSequence.current = 0;
  }, [identity]);

  const value = useMemo<LearningContextValue>(
    () => ({
      ready,
      identity,
      projection,
      recentEvents,
      emit,
      emitMany,
      saveCheckpoint,
      resetLocalLearner,
    }),
    [ready, identity, projection, recentEvents, emit, emitMany, saveCheckpoint, resetLocalLearner],
  );

  if (!ready) {
    return (
      <main className="app-loading" aria-busy="true">
        <span className="brand-mark" aria-hidden="true">
          ✦
        </span>
        <p>Opening the Atlas…</p>
      </main>
    );
  }

  return <LearningContext.Provider value={value}>{children}</LearningContext.Provider>;
}

export function useLearning() {
  const context = useContext(LearningContext);
  if (!context) throw new Error("useLearning must be used inside LearningProvider.");
  return context;
}
